import { useNavigate } from "react-router-dom";

function TermsOfService() {
  const navigate = useNavigate();
  const handleContact = () => navigate("/contact");

  return (
    <div>
      {/* Hero section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1>Terms of Service</h1>
          <p>
            These Terms of Service govern your access to and use of the
            AirArrange platform. By creating an account or making a booking, you
            agree to be bound by these Terms.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-5xl mx-auto px-6 py-16 space-y-12">
        {/* Overview */}
        <div className="bg-gray-50 border rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-3">Overview</h2>
          <ul className="list-disc pl-6 text-gray-600 space-y-2">
            <li>AirArrange is a digital marketplace for booking workspaces</li>
            <li>We are not a real estate agent, broker, or property manager</li>
            <li>Bookings are made directly between users and hosts</li>
            <li>
              Prices are fixed by hosts and not negotiated through the platform
            </li>
            <li>
              A platform service fee may apply and is disclosed at checkout
            </li>
          </ul>
        </div>

        {/* Platform role */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            1. Our Role as a Platform
          </h2>
          <p className="text-gray-600">
            AirArrange operates an online marketplace that enables users to
            discover and book workspaces offered by third‑party hosts.
            AirArrange does not own, lease, manage, or operate any workspace
            listed on the platform.
          </p>
          <p className="text-gray-600 mt-2">
            AirArrange does not act as an agent, broker, representative, or
            fiduciary for any user or host and does not negotiate booking terms,
            pricing, or conditions on behalf of any party.
          </p>
        </div>

        {/* Eligibility */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            2. Eligibility and Accounts
          </h2>
          <ul className="list-disc pl-6 text-gray-600 space-y-2">
            <li>You must be at least 18 years old to use the platform</li>
            <li>You must provide accurate and up‑to‑date information</li>
            <li>You are responsible for all activity under your account</li>
            <li>
              We may suspend or terminate accounts that breach these Terms
            </li>
          </ul>
        </div>

        {/* Listings and pricing */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            3. Listings and Pricing
          </h2>
          <ul className="list-disc pl-6 text-gray-600 space-y-2">
            <li>All workspace details and prices are set by hosts</li>
            <li>
              Prices displayed on the platform are fixed at the time of booking
            </li>
            <li>AirArrange does not influence or recommend pricing</li>
            <li>Hosts may update listings prior to a booking being made</li>
          </ul>
        </div>

        {/* Bookings */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">4. Bookings</h2>
          <ul className="list-disc pl-6 text-gray-600 space-y-2">
            <li>A booking is formed directly between the user and the host</li>
            <li>
              Bookings are confirmed only once payment is successfully processed
            </li>
            <li>AirArrange is not a party to the booking agreement</li>
            <li>Users agree to comply with host rules and conditions</li>
          </ul>
        </div>

        {/* Fees */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">5. Fees and Payments</h2>
          <ul className="list-disc pl-6 text-gray-600 space-y-2">
            <li>
              AirArrange may charge a platform service fee for use of the
              platform
            </li>
            <li>Any applicable fees are disclosed before checkout</li>
            <li>
              Payments are processed via third‑party providers such as Stripe
            </li>
            <li>AirArrange does not store credit card information</li>
            <li>Taxes are the responsibility of the relevant user or host</li>
          </ul>
        </div>

        {/* Cancellations */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            6. Cancellations and Refunds
          </h2>
          <ul className="list-disc pl-6 text-gray-600 space-y-2">
            <li>
              Each booking is subject to the cancellation policy set by the host
            </li>
            <li>
              Cancellation terms are displayed prior to booking confirmation
            </li>
            <li>Refund eligibility is determined by the applicable policy</li>
            <li>AirArrange does not override host cancellation terms</li>
          </ul>
        </div>

        {/* Conduct */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">7. User Conduct</h2>
          <ul className="list-disc pl-6 text-gray-600 space-y-2">
            <li>You must use the platform lawfully and respectfully</li>
            <li>
              You must not misuse messaging or attempt off‑platform
              circumvention
            </li>
            <li>You must not submit misleading or unlawful content</li>
            <li>We may remove content or accounts that breach these Terms</li>
          </ul>
        </div>

        {/* Liability */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            8. Liability and Disclaimers
          </h2>
          <p className="text-gray-600">
            To the maximum extent permitted by law, AirArrange is not liable for
            losses arising from workspace condition, host conduct, booking
            disputes, or user actions.
          </p>
          <p className="text-gray-600 mt-2">
            Our total liability is limited to the amount of platform fees paid
            by you in relation to the relevant booking.
          </p>
        </div>

        {/* Termination */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">9. Termination</h2>
          <ul className="list-disc pl-6 text-gray-600 space-y-2">
            <li>You may close your account at any time</li>
            <li>
              We may suspend or terminate access for breach of these Terms
            </li>
            <li>Termination does not affect existing bookings</li>
          </ul>
        </div>

        {/* Governing law */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">10. Governing Law</h2>
          <p className="text-gray-600">
            These Terms are governed by the laws of New South Wales, Australia.
            Any disputes are subject to the exclusive jurisdiction of the courts
            of New South Wales.
          </p>
        </div>

        {/* Contact */}
        <div className="border-t pt-8">
          <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>
          <button className="secondary-button" onClick={handleContact}>
            Contact Us
          </button>
        </div>
      </section>
    </div>
  );
}

export default TermsOfService;
